import { FC, ReactNode, useState } from 'react';
import { Badge } from './Badge';
import { DuoBadgeCheckIcon } from '../theme/Icons';
import { IonButton, IonIcon } from '@ionic/react';
import { close } from 'ionicons/icons';
import { IThemeColor } from '../theme/IThemeColor';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { useNudgeStorage } from '../utils/useNudgeStorage';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
    '@keyframes hide': {
        from: { opacity: 1 },
        to: { opacity: 0 }
    },

    hiding: {
        animation: '$hide ease 0.5s',
        opacity: 0
    }
});

type INudgeBadgeProps = {
    message: string | ReactNode;
    excludeIcon?: boolean;
    color?: IThemeColor;
    buttonLabel?: string;
    buttonClickHandler?: () => void;
    buttons?: {
        id?: string;
        label: string;
        clickHandler: () => void;
        'data-type'?: string;
    }[],
    'data-type'?: string;
    allowAsViewer?: boolean;
    dismissableStorageId?: string;
};

export const NudgeBadge: FC<INudgeBadgeProps> = ({
    message,
    excludeIcon = false,
    color = 'success',
    buttonLabel,
    buttonClickHandler = () => {},
    buttons,
    'data-type': dataType = 'edit-nudge-badge',
    allowAsViewer = false,
    dismissableStorageId
}) => {
    const classes = useStyles();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const [hiding, setHiding] = useState(false);
    const [visible, dismiss] = useNudgeStorage({ nudgeId: dismissableStorageId });

    const isViewer = !careMapState.hasPermissionTo('put');
    if (!visible || (isViewer && !allowAsViewer)) {
        return null;
    }

    const onDismiss = () => {
        setHiding(true);
        setTimeout(() => dismiss(), 500);
    };

    return (
        <div style={{margin: '20px 0'}} data-type={dataType} className={hiding ? classes.hiding : ''}>
            <Badge color={color} size="small">
                <div style={{display: 'flex', alignItems: 'center'}}>
                    {!excludeIcon && (
                        <div style={{padding: 12}}>
                            <DuoBadgeCheckIcon color={color} size="xlarge" />
                        </div>
                    )}
                    <div style={{flexGrow: 1, padding: 12}} data-type="message">{message}</div>

                    {dismissableStorageId && (
                        <IonButton
                            color="medium"
                            fill="clear"
                            shape="round"
                            size="small"
                            onClick={onDismiss}
                            style={{alignSelf: 'start', '--padding-start': '2px', '--padding-end': '2px'}}
                            data-id="nudge-badge-close"
                        >
                            <IonIcon slot="icon-only" icon={close} />
                        </IonButton>
                    )}
                </div>
                {buttonLabel && buttonClickHandler ? (
                    <div style={{padding: '10px 50px 10px 50px'}}>
                        <IonButton expand="block" onClick={buttonClickHandler}>{buttonLabel}</IonButton>
                    </div>
                ) : buttons && (
                    <div style={{padding: '10px 50px 10px 50px', display: 'flex', flexDirection: 'column', gap: 4}}>
                        {buttons.map((btn, idx) => (
                            <IonButton
                                id={btn.id}
                                key={`key-${idx}`}
                                expand="block"
                                onClick={btn.clickHandler}
                                data-type={btn['data-type']}
                            >{btn.label}</IonButton>
                        ))}
                    </div>
                )}
            </Badge>
        </div>
    );
};
